import { useState } from 'react';
import { CircleSlash } from 'lucide-react';
import { toast } from 'sonner';
import type { RunDetail, RunSummary } from '@shared/types/factory';
import { confirmDialog } from '@/components/ConfirmDialog';
import { StatusBadge } from '@/components/StatusBadge';
import { Button } from '@/components/ui/button';
import { describeApiError } from '@/lib/api-errors';
import { formatDateTime } from '@/lib/formatters';
import { closeRun } from '@/lib/runs-api';
import { runRef } from './run-status';

/**
 * SF-T-279: Close a run a person no longer wants (a batch started by mistake, still asking for answers), with every run
 * in its T3 thread: they were one conversation. After a confirmation naming them, the ones still going are cancelled,
 * none is carried on again, their open questions close unanswered, and their tasks stop waiting on the people they
 * asked. Their pull requests and branches stay. For people who can start runs; hidden once it's closed.
 */
export function CloseRunButton({
  run,
  project,
  size = 'sm',
  onClosed,
  closeFn = closeRun,
}: {
  run: Pick<RunSummary, 'id' | 'number' | 'closed' | 'thread_runs'>;
  project: string;
  size?: 'xs' | 'sm';
  /** Called with the run as it is now, once closed (or found closed already). */
  onClosed?: (run: RunDetail | null) => void;
  /** The gallery's stand-in for the request. */
  closeFn?: typeof closeRun;
}) {
  const [closing, setClosing] = useState(false);
  if (run.closed) return null;
  const others = Math.max(0, run.thread_runs - 1);
  const ref = runRef(run.number);
  const close = async () => {
    const ok = await confirmDialog({
      title: others ? `Close ${ref} and the ${others === 1 ? 'other run' : `${others} other runs`} in its thread?` : `Close ${ref}?`,
      description: [
        others
          ? `${others === 1 ? 'The other run' : `The other ${others} runs`} carried on ${ref}'s conversation, so ${others === 1 ? 'it closes' : 'they all close'} with it.`
          : null,
        'Runs still going are cancelled, as Cancel does: their T3 Code thread is left alone, so stop it in T3 Code if it is still working. None is carried on again: the next run of a task starts a new agent.',
        'Their open questions close unanswered, and their tasks stop waiting on the people they asked. Nothing is sent to the agent; pull requests and branches stay as they are.',
      ]
        .filter(Boolean)
        .join(' '),
      confirmLabel: others ? `Close ${others + 1} runs` : `Close ${ref}`,
      cancelLabel: 'Keep it',
      destructive: true,
    });
    if (!ok) return;
    setClosing(true);
    const res = await closeFn(run.id, { project });
    setClosing(false);
    if (res.code === 'RUN_CLOSED') {
      toast.info(`${ref} was closed already`);
      onClosed?.(null);
      return;
    }
    if (!res.data) {
      toast.error(`Couldn't close ${ref}: ${describeApiError(res)}`);
      return;
    }
    const closed = res.data.closed.runs;
    toast.success(closed.length > 1 ? `Closed ${closed.length} runs, ${ref}'s thread` : `Closed ${ref}`);
    onClosed?.(res.data.run);
  };
  return (
    <Button
      size={size}
      variant="outline"
      loading={closing}
      loadingText="Closing…"
      onClick={() => void close()}
      data-hint="Retire this run and its thread: it's never carried on, and its open questions close unanswered"
    >
      <CircleSlash />
      Close run
    </Button>
  );
}

/** SF-T-279: marks a closed run (with when, and who closed it), on the task's runs and the run's page. */
export function RunClosedBadge({ closed, size = 'sm' }: { closed: NonNullable<RunSummary['closed']>; size?: 'sm' | 'default' }) {
  return (
    <StatusBadge
      tone="neutral"
      size={size}
      shape="square"
      data-hint={`Closed ${formatDateTime(closed.at)}${closed.by ? ` by ${closed.by}` : ''}: never carried on, its open questions closed unanswered`}
      data-testid="run-closed"
    >
      Closed
    </StatusBadge>
  );
}
